import Eyebrow from "./Eyebrow";
import { CheckCircle2 } from "lucide-react";

const EXPERIENCES = [
  {
    index: "01",
    role: "Maker / Indie App Maker",
    company: "schnipsel.app",
    url: "https://schnipsel.app",
    period: "Seit September 2026",
    status: { text: "Aktuell", tone: "positive" },
    stack: ["Indie-Entwicklung", "App-Design", "Produktentwicklung"],
    achievement: "Eigene Apps von der Idee bis zur Veröffentlichung unter schnipsel.app",
    description:
      "Entwicklung und Veröffentlichung eigener Apps als unabhängiger Maker: von der Idee über Design und Umsetzung bis zum Release und der Weiterentwicklung.",
    projects: [
      {
        name: "Schnipsel",
        label: "schnipsel.app",
        url: "https://schnipsel.app",
        text: "Digitales Scrapbook für iPhone, das Fotos und kleine Erinnerungsstücke wie Tickets oder Postkarten als Sticker ausschneidet und zu Erinnerungs-Boards zusammenstellt.",
      },
      {
        name: "Wanderhof",
        label: "wanderfarm.app",
        url: "https://wanderfarm.app",
        text: "Gemütliches Pixel-Farmspiel für iPhone und Apple Watch, das tägliche Schritte aus Apple Health in Energie für einen Bauernhof in einem Schweizer Bergtal verwandelt.",
      },
      {
        name: "Neurimo",
        label: "neurimo.ch",
        url: "https://neurimo.ch",
        text: "Plattform für Menschen mit ADHS und Autismus in der ganzen Schweiz, die geprüfte Unterstützungsangebote, Austausch in einer Community und gemeinsame Treffen an einem Ort bündelt.",
      },
    ],
  },
  {
    index: "02",
    role: "IT Support Specialist",
    company: "Stiftung Transfair",
    period: "Juni 2026 – August 2026",
    status: { text: "Zuletzt · bis Aug. 2026", tone: "accent" },
    stack: ["IT-Support", "IT-Transformation", "Infrastruktur-Modernisierung"],
    achievement: "Interner IT-Support & aktive Mitwirkung an der IT-Transformation von Transfair",
    description:
      "Interner IT-Support für Mitarbeitende bei Transfair sowie Unterstützung bei der IT-Transformation und Modernisierung der IT-Infrastruktur.",
  },
  {
    index: "03",
    role: "Cloud Support Specialist",
    company: "Isolutions AG",
    period: "September 2020 – Juni 2025",
    stack: ["Microsoft 365", "Azure AD", "Intune", "Exchange Online", "SharePoint Online", "Teams"],
    achievement: "SPOC für 1st-/2nd-Level-Support und M365-Administration für Kunden der Energie- und Bankenbranche",
    description:
      "Administration von Microsoft 365 (Exchange Online, Teams, SharePoint Online), Endpoint-Management mit Intune und Single Point of Contact für 1st-/2nd-Level-Support. Koordination kritischer Incidents und Mitwirkung an M365-Rollouts für Kunden der Energie- und Bankenbranche – bei reibungslosem IT-Betrieb vor Ort.",
  },
  {
    index: "04",
    role: "IT Service Desk Specialist",
    company: "CSL Behring AG",
    period: "Oktober 2018 – September 2020",
    stack: ["1st-Level-Support", "Deutsch & Englisch", "Endnutzer-Schulungen", "Active Directory", "Office 365", "SAP"],
    achievement: "Internationaler Support für Mitarbeitende in Europa, den USA und Australien, auf Deutsch und Englisch",
    description:
      "First-Level-Support für Mitarbeitende in Europa, den USA und Australien inkl. Fehleranalyse und Diagnose. Applikationsverteilung, Endnutzer-Schulungen und Benutzerverwaltung in Active Directory, Office 365 und diversen SAP-Systemen.",
  },
  {
    index: "05",
    role: "Specialist",
    company: "Apple Retail Switzerland",
    period: "Oktober 2010 – August 2014",
    stack: ["Kundenberatung", "Customer Experience", "Hardware-Diagnose", "macOS", "iOS"],
    achievement: "Kundenberatung mit hoher Zufriedenheit bei Hardware- & Softwarediagnosen",
    description:
      "Beratung und Betreuung von Kunden durch Erkennen individueller Bedürfnisse und passende Lösungen. Verständliche Erklärung komplexer technischer Sachverhalte sowie Diagnose und Behebung von Hardware- und Softwareproblemen.",
  },
];

const statusColors = {
  positive: "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
  accent:   "text-accent bg-accent/10 border-accent/20",
};

const ExperienceSection = () => (
  <section id="erfahrung" className="border-b py-16 sm:py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 space-y-12 lg:space-y-16">

      <div className="space-y-2">
        <Eyebrow>Berufserfahrung</Eyebrow>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
          Wo ich war und was ich gelernt habe
        </h2>
      </div>

      {/* Timeline */}
      <div className="relative space-y-0">
        {/* Vertical line */}
        <div className="absolute left-[19px] top-3 bottom-3 w-px bg-border hidden sm:block" />

        {EXPERIENCES.map((exp, i) => (
          <div key={i} className="relative sm:pl-12 pb-12 last:pb-0">
            {/* Dot */}
            <div className="absolute left-0 top-1 size-10 rounded-full border bg-card
                             flex items-center justify-center text-xs font-bold
                             text-muted-foreground hidden sm:flex shadow-sm">
              {exp.index}
            </div>

            <div className="rounded-2xl border bg-card p-6 shadow-sm space-y-4">
              {/* Header */}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-foreground text-lg leading-tight">
                    {exp.role}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {exp.url ? (
                      <a
                        href={exp.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-foreground transition-colors"
                      >
                        {exp.company}
                      </a>
                    ) : (
                      exp.company
                    )}{" "}
                    · {exp.period}
                  </p>
                </div>
                {exp.status && (
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusColors[exp.status.tone]}`}>
                    {exp.status.text}
                  </span>
                )}
              </div>

              {/* Achievement */}
              <div className="flex items-start gap-2">
                <CheckCircle2 className="size-4 text-accent mt-0.5 shrink-0" strokeWidth={1.8} />
                <p className="text-sm font-medium text-foreground">{exp.achievement}</p>
              </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground leading-relaxed">
                {exp.description}
              </p>

              {/* Current projects */}
              {exp.projects && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Aktuelle Projekte
                  </p>
                  <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {exp.projects.map((project) => (
                      <li key={project.url} className="rounded-xl border bg-muted/40 p-4 space-y-1">
                        <p className="text-sm font-medium text-foreground">
                          {project.name}{" "}
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-normal text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                          >
                            {project.label}
                          </a>
                        </p>
                        <p className="text-sm text-muted-foreground leading-relaxed">{project.text}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stack pills */}
              <div className="flex flex-wrap gap-2 pt-1">
                {exp.stack.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs font-medium rounded-full
                               border bg-muted text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default ExperienceSection;
